import React, { useState } from 'react';
import { Button, DatePicker, Dropdown, ErrorText, HijriDatePicker, InputField, PhoneInput, RadioButton, Text, TextArea } from '../../../components/SecondaryMarket';
import { fontSizes } from '../../../constants/constants';
import dynamicfunction from '../../../utils/dynamicfunction';
import ViewReverse from '../../../components/ViewReverse';
import dayjs from 'dayjs';
import ViewItems from '../../../components/ViewItems';
import icons from '../../../constants/icons';
import { ReactSVG } from 'react-svg';
import { cn, formatNumbers, getContentKeyAccToLang } from '../../../utils/utils';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import { general_information_schema, general_information_validation_schema, empty_manager, manager_add_validation_schema } from '../../../validations_schemas/general_information_validation_schema';
import { isEmptyValue, alphabet_regix, arabic_text_regix, getRejectedFieldData, numeric_decimal_regix, numeric_integer_regix, splitPhoneNumberAndCode } from '../../../utils/functions';
import CustomToolTip from '../../../components/ToolTip';
import ManagerEditModal from './ManagerEditModal';

const GeneralInformation = ({ isKYC, isOpen = true, setStep, contentKey, formRef, percentage }) => {
  const { TextWeight, isArabic } = dynamicfunction();
  const [editManagerModal, setEditManagerModal] = useState(false);
  const [editManagerIndex, setEditManagerIndex] = useState(null);

  const corpKycContent = useSelector((state) => state.content?.corpKycContent);
  const corpAOContent = useSelector((state) => state.content?.corpAOContent);
  const { corporate_kyc_data, corporate_dropdown_data, rejection_summary, is_rejected, field_codes, corporate_loading } = useSelector((state) => state.corporate);

  const getContentKey = (key) => {
    const content = isKYC ? corpKycContent : corpAOContent;
    return getContentKeyAccToLang(content?.[contentKey]?.data?.attributes, key, isArabic);
  };

  const rejectedFields = rejection_summary?.find(item => item?.sectionId === 2)?.rejectedFields || [];
  const rejectedField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'legalStructure');

  const generalInformationData = isKYC ? corporate_kyc_data?.kycGeneralInformation : corporate_kyc_data?.accountOpeningGeneralInformation;
  const enable_in_accountOpening = !isKYC ? false : is_rejected ? false : true;

  const handleEditManagerClick = (index) => {
    setEditManagerIndex(index);
    setEditManagerModal(true);
  };

  const handleCloseEditManagerModal = () => {
    setEditManagerModal(false);
    setEditManagerIndex(null);
  };

  const getEditManagerInitialValues = () => {
    const formValues = formRef?.current?.values;
    const manager = formValues?.managerList?.[editManagerIndex];

    return {
      ...empty_manager,
      ...manager,
    };
  };

  const handleEditManagerSubmit = (managerValues, { resetForm }) => {
    const formValues = formRef?.current?.values;
    const currentManagers = formValues?.managerList || [];
    const updatedManagers = [...currentManagers];
    updatedManagers.splice(editManagerIndex, 1, managerValues);
    formRef?.current?.setFieldValue('managerList', updatedManagers);
    resetForm({ values: getEditManagerInitialValues() });
    handleCloseEditManagerModal();
  };

  return (
    <div className='w-full'>
      <ViewReverse className={cn('w-full items-center gap-2 justify-between', isKYC && 'cursor-pointer')} onClick={() => isKYC && setStep(1)}>

        <ViewReverse className="flex items-center gap-2">
          <Text text={getContentKey('general_information')} weight={TextWeight.headingRegular} className={'text-dark-blue'} size={fontSizes.h30} />
          {rejectedFields?.length > 0 && <ReactSVG src={icons.heading_info_outline_icon} />}
        </ViewReverse>

        <ViewReverse className={'items-center gap-2'}>
          <Text text={`${formatNumbers(percentage) ?? 0}%`} className={'text-dark-blue'} size={fontSizes.h14} />
          {isKYC && <ReactSVG src={icons.downArrow} className={cn(isOpen ? 'rotate-180' : 'rotate-0')} />}
        </ViewReverse>
      </ViewReverse>

      <Formik
        innerRef={formRef}
        validationSchema={general_information_validation_schema}
        initialValues={general_information_schema(generalInformationData)}
        enableReinitialize
        validateOnChange={false}
        validateOnMount={false}
        validateOnBlur={false}
        onSubmit={(values) => { }}
      >
        {({ setFieldValue, setFieldError, values, errors }) => {
          const main_activity_array = Array.isArray(values?.mainActivityList) && values?.mainActivityList?.length > 0 ? values?.mainActivityList : []
          const main_activity_names = main_activity_array?.map((item) => (isArabic ? item?.nameAr?.trim()?.length > 0 ? item?.nameAr : item?.nameEn : item?.nameEn?.trim()?.length > 0 ? item?.nameEn : item?.nameAr)).join(' , ');
          const main_activity_first_name = isArabic ? main_activity_array?.[0]?.nameAr?.trim()?.length > 0 ? main_activity_array?.[0]?.nameAr : main_activity_array?.[0]?.nameEn : main_activity_array?.[0]?.nameEn?.trim()?.length > 0 ? main_activity_array?.[0]?.nameEn : main_activity_array?.[0]?.nameAr
          return (isOpen && (
            <>
              {isKYC && <div className='mt-8 h-[1px] bg-black/10' />}
              <div className='space-y-6'>
                <ViewReverse className={'gap-6 mt-8'} isResponsive>
                  <InputField
                    label={getContentKey('name_english')}
                    isRequired
                    isDisabled={enable_in_accountOpening}
                    placeholder={getContentKey('enter_name_english')}
                    value={values?.commercialRegistrationNameEn}
                    maxLength={50}
                    onChange={(text) => {
                      if (alphabet_regix.test(text)) {
                        setFieldValue('commercialRegistrationNameEn', text);
                        setFieldError('commercialRegistrationNameEn', null);
                      }
                    }}
                    error={getContentKey(errors?.commercialRegistrationNameEn)}
                    remarksMetadata={values?.commercialRegistrationNameEn !== generalInformationData?.commercialRegistrationNameEn ? null : {
                      sectionId: 2,
                      key: 'commercialRegistrationNameEn',
                    }}
                  />

                  <InputField
                    label={getContentKey('name_arabic')}
                    isRequired
                    isArabicAllowed
                    placeholder={getContentKey('enter_name_arabic')}
                    value={values?.commercialRegistrationName}
                    isDisabled={is_rejected ? false : generalInformationData?.isCommercialRegistrationNameFetchByWathq}
                    onChange={(text) => {
                      if (arabic_text_regix.test(text)) {
                        setFieldValue('commercialRegistrationName', text);
                        setFieldError('commercialRegistrationName', null);
                      }
                    }}
                    error={getContentKey(errors?.commercialRegistrationName)}
                    remarksMetadata={values?.commercialRegistrationName !== generalInformationData?.commercialRegistrationName ? null : {
                      sectionId: 2,
                      key: 'commercialRegistrationName',
                    }}
                  />
                  <div className='w-full hidden md:flex' />
                </ViewReverse>

                <ViewReverse className={'gap-6'} isResponsive>
                  <InputField
                    label={getContentKey('cr_number')}
                    isRequired
                    isLabelHeight
                    labelDivClass={'md:h-9'}
                    isDisabled={is_rejected ? false : generalInformationData?.isCommercialRegistrationNoFetchByWathq}
                    placeholder={getContentKey('enter_cr_number')}
                    value={values?.commercialRegistrationNo}
                    onChange={(text) => {
                      if (numeric_integer_regix.test(text)) {
                        setFieldValue('commercialRegistrationNo', text);
                        setFieldError('commercialRegistrationNo', null);
                      }
                    }}
                    error={getContentKey(errors?.commercialRegistrationNo)}
                    remarksMetadata={values?.commercialRegistrationNo !== generalInformationData?.commercialRegistrationNo ? null : {
                      sectionId: 2,
                      key: 'commercialRegistrationNo',
                    }}
                  />
                  <DatePicker
                    label={getContentKey('expiry_date_cr_certificate')}
                    isRequired
                    isLabelHeight
                    labelDivClass={'md:h-9'}
                    minDate={dayjs()}
                    isDisabled={is_rejected ? false : generalInformationData?.isExpiryDateOfCRGRGFetchByWathq}
                    placeholder={getContentKey('expiry_date_cr_certificate')}
                    selectedDate={values?.expiryDateOfCRGRG}
                    onChange={(date) => {
                      setFieldValue('expiryDateOfCRGRG', date ? dayjs(date).format('YYYY/MM/DD') : '');
                      setFieldError('expiryDateOfCRGRG', null);
                    }}
                    error={getContentKey(errors?.expiryDateOfCRGRG)}
                    remarksMetadata={values?.expiryDateOfCRGRG !== generalInformationData?.expiryDateOfCRGRG ? null : {
                      sectionId: 2,
                      key: 'expiryDateOfCRGRG',
                    }}
                  />
                  <HijriDatePicker
                    label={getContentKey('expiry_date_cr_certificate_hijri')}
                    isRequired
                    isLabelHeight
                    labelDivClass={'md:h-9'}
                    minDate={dayjs()}
                    isDisabled={is_rejected ? false : generalInformationData?.isExpiryDateOfCRFetchByWathq}
                    placeholder={getContentKey('expiry_date_cr_certificate')}
                    selectedDate={values?.expiryDateOfCR}
                    onChange={(date) => {
                      setFieldValue('expiryDateOfCR', date);
                      setFieldError('expiryDateOfCR', null);
                    }}
                    error={getContentKey(errors?.expiryDateOfCR)}
                    remarksMetadata={values?.expiryDateOfCR !== generalInformationData?.expiryDateOfCR ? null : {
                      sectionId: 2,
                      key: 'expiryDateOfCR',
                    }}
                  />
                </ViewReverse>

                <ViewReverse className={'gap-6'} isResponsive>
                  <InputField
                    label={getContentKey('unified_number')}
                    isRequired
                    isDisabled={is_rejected ? false : generalInformationData?.isUnifiedNumberFetchByWathq}
                    placeholder={getContentKey('enter_unified_number')}
                    value={values?.unifiedNumber}
                    onChange={(text) => {
                      setFieldValue('unifiedNumber', text);
                      setFieldError('unifiedNumber', null);
                    }}
                    error={getContentKey(errors?.unifiedNumber)}
                    remarksMetadata={values?.unifiedNumber !== generalInformationData?.unifiedNumber ? null : {
                      sectionId: 2,
                      key: 'unifiedNumber',
                    }}
                  />
                  <Dropdown
                    label={getContentKey('country_of_registration')}
                    placeholder={getContentKey('select_country_of_registration')}
                    valueKey={'name'}
                    isDisabled={enable_in_accountOpening}
                    value={values?.countryOfRegistration ? corporate_dropdown_data?.country?.find(item => item?.id === values?.countryOfRegistration) : null}
                    options={corporate_dropdown_data?.country ?? []}
                    isRequired
                    onSelect={(val) => {
                      setFieldValue('countryOfRegistration', val?.id);
                      setFieldError('countryOfRegistration', null);
                    }}
                    error={getContentKey(errors?.countryOfRegistration)}
                    remarksMetadata={values?.countryOfRegistration !== generalInformationData?.countryOfRegistration ? null : {
                      sectionId: 2,
                      key: 'countryOfRegistration',
                    }}
                  />
                  <DatePicker
                    label={getContentKey('date_of_incorporation')}
                    isRequired
                    minDate={dayjs()}
                    selectedDate={values?.dateOfIncorporationGRG}
                    isDisabled={is_rejected ? false : generalInformationData?.isDateOfIncorporationGRGFetchByWathq}
                    placeholder={getContentKey('date_of_incorporation')}
                    onChange={(date) => {
                      setFieldValue('dateOfIncorporationGRG', date ? dayjs(date).format('YYYY/MM/DD') : '');
                      setFieldError('dateOfIncorporationGRG', null);
                    }}
                    error={getContentKey(errors?.dateOfIncorporationGRG)}
                    remarksMetadata={values?.dateOfIncorporationGRG !== generalInformationData?.dateOfIncorporationGRG ? null : {
                      sectionId: 2,
                      key: 'dateOfIncorporationGRG',
                    }}
                  />
                </ViewReverse>

                <ViewReverse className={'gap-6'} isResponsive>
                  <HijriDatePicker
                    label={getContentKey('date_of_incorporation_hijri')}
                    isRequired
                    minDate={dayjs()}
                    selectedDate={values?.dateOfIncorporation}
                    isDisabled={is_rejected ? false : generalInformationData?.isDateOfIncorporationFetchByWathq}
                    placeholder={getContentKey('date_of_incorporation')}
                    onChange={(date) => {
                      setFieldValue('dateOfIncorporation', date);
                      setFieldError('dateOfIncorporation', null);
                    }}
                    error={getContentKey(errors?.dateOfIncorporation)}
                    remarksMetadata={values?.dateOfIncorporation !== generalInformationData?.dateOfIncorporation ? null : {
                      sectionId: 2,
                      key: 'dateOfIncorporation',
                    }}
                  />
                  <Dropdown
                    label={getContentKey('country_practicing_business')}
                    placeholder={getContentKey('select_country_practicing_business')}
                    valueKey={'name'}
                    isDisabled={enable_in_accountOpening}
                    value={values?.countryOfPracticingBusiness ? corporate_dropdown_data?.country?.find(item => item?.id === values?.countryOfPracticingBusiness) : null}
                    options={corporate_dropdown_data?.country ?? []}
                    onSelect={(val) => {
                      setFieldValue('countryOfPracticingBusiness', val?.id);
                      setFieldError('countryOfPracticingBusiness', null);
                    }}
                    error={getContentKey(errors?.countryOfPracticingBusiness)}
                    remarksMetadata={values?.countryOfPracticingBusiness !== generalInformationData?.countryOfPracticingBusiness ? null : {
                      sectionId: 2,
                      key: 'countryOfPracticingBusiness',
                    }}
                  />
                  <InputField
                    label={getContentKey('paidup_capital')}
                    isRequired
                    placeholder={getContentKey('enter_paidup_capital')}
                    className={'gap-2'}
                    isRightSymbol
                    maxLength={9}
                    isDisabled={is_rejected ? false : generalInformationData?.isPaidUpCapitalFetchByWathq}
                    value={!is_rejected && generalInformationData?.isPaidUpCapitalFetchByWathq && values?.paidUpCapital
                      ? Number(values?.paidUpCapital).toLocaleString('en-US', { maximumFractionDigits: 2 })
                      : values?.paidUpCapital}
                    onChange={(text) => {
                      if (numeric_decimal_regix.test(text) && !/^0+$/.test(text)) {
                        setFieldValue('paidUpCapital', text);
                        setFieldError('paidUpCapital', null);
                      }
                    }}
                    error={getContentKey(errors?.paidUpCapital)}
                    remarksMetadata={values?.paidUpCapital !== generalInformationData?.paidUpCapital ? null : {
                      sectionId: 2,
                      key: 'paidUpCapital',
                    }}

                  />
                </ViewReverse>

                <ViewReverse className={'gap-6'} isResponsive>
                  <InputField
                    label={getContentKey('annual_turnover')}
                    isRequired
                    placeholder={getContentKey('enter_annual_turnover')}
                    className={'gap-2'}
                    isRightSymbol
                    maxLength={15}
                    value={values?.annualTurnover}
                    onChange={(text) => {
                      if (numeric_decimal_regix.test(text) && !/^0+$/.test(text)) {
                        setFieldValue('annualTurnover', text);
                        setFieldError('annualTurnover', null);
                      }
                    }}
                    error={getContentKey(errors?.annualTurnover)}
                    remarksMetadata={values?.annualTurnover !== generalInformationData?.annualTurnover ? null : {
                      sectionId: 2,
                      key: 'annualTurnover',
                    }}
                  />
                  <InputField
                    label={getContentKey('number_of_employees')}
                    placeholder={getContentKey('enter_number_of_employees')}
                    value={values?.noOfEmployees}
                    maxLength={7}
                    onChange={(text) => {
                      if (numeric_integer_regix.test(text) && !/^0+$/.test(text)) {
                        setFieldValue('noOfEmployees', text);
                        setFieldError('noOfEmployees', null);
                      }
                    }}
                    error={getContentKey(errors?.noOfEmployees)}
                    remarksMetadata={values?.noOfEmployees !== generalInformationData?.noOfEmployees ? null : {
                      sectionId: 2,
                      key: 'noOfEmployees',
                    }}
                  />

                  <CustomToolTip title={main_activity_names} isArabic={true}>
                    <span className='w-full'>
                      <InputField
                        label={getContentKey('main_activity')}
                        isRequired
                        placeholder={getContentKey('enter_main_activity')}
                        isArabicAllowed
                        value={`${main_activity_first_name} ${main_activity_array.length > 1 ? `+ ${main_activity_array?.slice(1)?.length ?? 0}` : ""} `}
                        isDisabled
                        onChange={(text) => {
                          setFieldValue('mainActivityList', text);
                          setFieldError('mainActivityList', null);
                        }}
                        error={getContentKey(errors?.mainActivityList)}
                        remarksMetadata={values?.mainActivityList !== generalInformationData?.mainActivityList ? null : {
                          sectionId: 2,
                          key: 'mainActivityList',
                        }}
                      />
                    </span>
                  </CustomToolTip>

                  {/* <InputField
                    label={getContentKey('main_activity')}
                    isRequired
                    // placeholder={getContentKey('enter_main_activity')}
                    // value={values?.mainActivityList}
                    value={main_activity_array}
                    isDisabled={is_rejected ? false : true}
                    onChange={(text) => {
                      setFieldValue('mainActivityList', text);
                      setFieldError('mainActivityList', null);
                    }}
                    error={getContentKey(errors?.mainActivityList)}
                    remarksMetadata={values?.mainActivityList !== generalInformationData?.mainActivityList ? null : {
                      sectionId: 2,
                      key: 'mainActivityList',
                    }}
                  /> */}
                </ViewReverse>

                <ViewReverse className={'gap-6'} isResponsive>
                  <ViewItems className={'w-full'}>
                    <PhoneInput
                      label={getContentKey('phone_number')}
                      placeholder={getContentKey('enter_phone_number')}
                      value={values?.phoneNumber}
                      isDisabled={is_rejected ? false : generalInformationData?.isPhoneNumberFetchByWathq}
                      country={values?.phoneCountry}
                      setCountry={(country) => setFieldValue('phoneCountry', country)}
                      onChange={(text, isValid) => {
                        if (numeric_integer_regix.test(text)) {
                          setFieldValue('phoneNumber', text, false);
                        }
                        if (!isValid) {
                          setFieldError('phoneNumber', 'invalid_phone_number');
                        } else {
                          setFieldError('phoneNumber', undefined);
                        }
                      }}
                      remarksMetadata={values?.phoneNumber !== splitPhoneNumberAndCode(generalInformationData?.phoneNumber)?.number ? null : {
                        sectionId: 2,
                        key: 'phoneNumber',
                      }}
                    />
                    <ErrorText text={getContentKey(errors?.phoneNumber)} />
                  </ViewItems>
                  <ViewItems className={'w-full'}>
                    <PhoneInput
                      label={getContentKey('fax_number')}
                      isRequired={false}
                      placeholder={getContentKey('enter_fax_number')}
                      value={values?.faxNumber}
                      isDisabled={is_rejected ? false : true}
                      country={values?.faxCountry}
                      setCountry={(country) => setFieldValue('faxCountry', country)}
                      onChange={(text, isValid) => {
                        if (numeric_integer_regix.test(text)) {
                          setFieldValue('faxNumber', text, false);
                        }
                        if (!isValid) {
                          setFieldError('faxNumber', 'invalid_phone_number');
                        } else {
                          setFieldError('faxNumber', undefined);
                        }
                      }}
                      remarksMetadata={values?.faxNumber !== splitPhoneNumberAndCode(generalInformationData?.faxNumber)?.number ? null : {
                        sectionId: 2,
                        key: 'faxNumber',
                      }}
                    />
                    <ErrorText text={getContentKey(errors?.faxNumber)} />
                  </ViewItems>
                  <div className='w-full' />
                </ViewReverse >

                <ViewItems className={'gap-2 w-full'}>
                  <Text text={getContentKey('legal_structure')} isEmpty={isEmptyValue(values?.legalStructure)} size={fontSizes.h14} className={'text-primary-light'} isRequired={true} />

                  <div className='grid grid-cols-1 sm:grid-cols-4 w-full gap-5' dir={isArabic ? 'rtl' : 'ltr'}>
                    {corporate_dropdown_data?.legalStructure?.map((item, index) => (
                      <React.Fragment key={item?.id || index}>
                        <ViewReverse className={cn('gap-4', corporate_dropdown_data?.legalStructure?.length === index + 1 ? 'sm:col-span-2' : '')} isReverse={false}>
                          <RadioButton
                            label={item?.name}
                            isReverse={false}
                            labelClasses={'whitespace-nowrap'}
                            selected={String(values?.legalStructure) === String(item?.id)}
                            onClick={() => {
                              if (String(values?.legalStructure) === String(item?.id)) {
                                setFieldValue('legalStructure', null);
                                setFieldValue('legalStructureOther', '');
                              } else {
                                setFieldValue('legalStructure', item?.id);
                              }
                              setFieldError('legalStructure', null);
                            }}
                            remarksMetadata={values?.legalStructure !== generalInformationData?.legalStructure ? null : {
                              sectionId: 2,
                              key: 'legalStructure',
                            }}
                          />
                          {index === 5 && String(values?.legalStructure) === '1088' && (
                            <InputField
                              isRequired
                              placeholder={getContentKey('type_here')}
                              value={values?.legalStructureOther}
                              isArabicAllowed
                              onChange={(text) => {
                                setFieldValue('legalStructureOther', text);
                                setFieldError('legalStructureOther', null);
                              }}
                              error={getContentKey(errors?.legalStructureOther)}
                              remarksMetadata={values?.legalStructure !== generalInformationData?.legalStructure ? null : {
                                sectionId: 2,
                                key: 'legalStructure',
                              }}
                            />
                          )}
                        </ViewReverse>
                        {index === 2 && <div className='hidden sm:flex' />}
                      </React.Fragment>
                    ))}
                  </div>
                  {
                    (values?.legalStructure === generalInformationData?.legalStructure) && rejectedField?.remarks?.length > 0 && (
                      <ViewReverse className='items-center gap-2 mt-2'>
                        <ReactSVG src={icons.field_info_outline} />
                        <ErrorText text={rejectedField?.remarks} className={'mt-0'} />
                      </ViewReverse>
                    )
                  }
                  <ErrorText text={getContentKey(errors?.legalStructure)} />
                </ViewItems>
              </div >

              <ViewItems className={'mt-12'}>
                <Text text={getContentKey('names_of_directors_executives')} isEmpty={isEmptyValue(values?.managerList)} size={fontSizes.h14} className={'text-primary-light'} isRequired />

                <div className='w-full max-w-full mt-5 overflow-x-scroll bg-light-background rounded-lg py-4 px-5'>
                  <ViewReverse className={'gap-4 mb-2 justify-between'}>
                    <Text text={getContentKey('name_heading')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-48 max-w-48'} />
                    <Text text={getContentKey('date_of_birth')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-48 max-w-48'} />
                    <Text text={getContentKey('identification_id')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-48 max-w-48'} />
                    <Text text={getContentKey('indentification_type')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-48 max-w-48'} />
                    <Text text={getContentKey('country')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-48 max-w-48'} />
                    {!isKYC && <Text text={getContentKey('action')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-16 max-w-16'} />}
                  </ViewReverse>

                  {values?.managerList?.map((item, index) => {
                    const country = corporate_dropdown_data?.country?.find(country => country?.id === item?.nationalityId);
                    const isEditable = !is_rejected && !generalInformationData?.IsManagerListFetchByWathq;

                    return (
                      <ViewReverse className={'gap-4 mt-3 justify-between'} key={index + item?.name}>
                        <Text text={item?.name} size={fontSizes.h14} className={'min-w-48 max-w-48 text-dark-grey/70'} />
                        <Text text={item?.birthDate} size={fontSizes.h14} className={'min-w-48 max-w-48 text-dark-grey/70'} />
                        <Text text={item?.identificationId} size={fontSizes.h14} className={'min-w-48 max-w-48 text-dark-grey/70'} />
                        <Text text={item?.identificationType} size={fontSizes.h14} className={'min-w-48 max-w-48 text-dark-grey/70'} />
                        <Text text={country?.name} size={fontSizes.h14} className={'min-w-48 max-w-48 text-dark-grey/70'} />
                        {!isKYC && (
                          <ViewReverse className={'gap-3 min-w-16 max-w-16'}>
                            <ReactSVG
                              src={icons.edit_icon}
                              className={cn(isEditable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50')}
                              onClick={() => isEditable && handleEditManagerClick(index)}
                            />
                            <ReactSVG
                              src={icons.delete_outline_icon}
                              className={cn(isEditable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50')}
                              onClick={() => {
                                isEditable && (
                                  setFieldValue(
                                    'managerList',
                                    (values?.managerList || []).filter((_, rowIndex) => rowIndex !== index),
                                  )
                                )
                              }}
                            />
                          </ViewReverse>
                        )}
                      </ViewReverse>
                    )
                  })}
                </div>
              </ViewItems>

              {!isKYC && (
                <div className='mt-6 bg-light-background p-6 rounded-lg skip-required-highlight'>
                  <Formik
                    initialValues={{ ...empty_manager }}
                    validateOnChange={false}
                    validateOnMount={false}
                    validateOnBlur={false}
                    validationSchema={manager_add_validation_schema}
                    onSubmit={(managerValues, { resetForm }) => {
                      const currentManagers = values?.managerList || [];
                      setFieldValue('managerList', [...currentManagers, { ...managerValues }]);
                      setFieldError('managerList', null);
                      resetForm();
                    }}
                  >
                    {({ values, setFieldValue: setManagerFieldValue, handleSubmit, resetForm, errors }) => {

                      return (
                        <>
                          <div className='space-y-6'>
                            <ViewReverse className={'gap-6 mt-2'} isResponsive>
                              <InputField
                                label={getContentKey('name_heading')}
                                isRequired
                                placeholder={getContentKey('name_heading')}
                                className={'bg-white'}
                                value={values?.name}
                                maxLength={50}
                                isDisabled={values?.isNameFetchByWathq}
                                onChange={(text) => {
                                  if (alphabet_regix.test(text)) {
                                    setManagerFieldValue('name', text);
                                  }
                                }}
                                error={getContentKey(errors?.name)}
                              />
                              <InputField
                                label={getContentKey("identification_id_label")}
                                isRequired
                                placeholder={getContentKey("identification_id_label")}
                                className={'bg-white'}
                                value={values?.identificationId}
                                maxLength={20}
                                isDisabled={values?.isIdentificationIdFetchByWathq}
                                onChange={(text) => {
                                  setManagerFieldValue('identificationId', text);
                                }}
                                error={getContentKey(errors?.identificationId)}
                              />
                              <InputField
                                label={getContentKey("indentification_type_label")}
                                isRequired
                                placeholder={getContentKey("indentification_type_label")}
                                className={'bg-white'}
                                value={values?.identificationType}
                                maxLength={50}
                                isDisabled={values?.isIdentificationTypeFetchByWathq}
                                onChange={(text) => {
                                  setManagerFieldValue('identificationType', text);
                                }}
                                error={getContentKey(errors?.identificationType)}
                                remarksMetadata={values?.identificationType !== corporate_kyc_data?.kycManagerList?.identificationType ? null : {
                                  sectionId: 2,
                                  key: 'identificationType',
                                }}
                              />
                            </ViewReverse>

                            <ViewReverse className={'gap-6 mt-8'} isResponsive>
                              <Dropdown
                                label={getContentKey("country_label")}
                                placeholder={getContentKey("country_label")}
                                valueKey={'name'}
                                value={values?.nationalityId ? corporate_dropdown_data?.country?.find(item => item?.id === values?.nationalityId) : null}
                                isRequired={false}
                                options={corporate_dropdown_data?.country ?? []}
                                isDisabled={values?.isNationalityIdFetchByWathq}
                                onSelect={(val) => {
                                  setManagerFieldValue('nationalityId', val?.id);
                                  setFieldError('nationalityId', null);
                                }}
                                error={getContentKey(errors?.nationalityId)}
                              />
                              <DatePicker
                                label={getContentKey("date_of_birth")}
                                className={"bg-white"}
                                isRequired
                                isLabelHeight
                                labelDivClass={'md:h-6'}
                                // minDate={dayjs()}
                                maxDate={dayjs()}
                                placeholder={getContentKey("date_birth_label")}
                                selectedDate={values?.birthDate}
                                isDisabled={values?.isBirthDateFetchByWathq}
                                onChange={(date) => {
                                  setManagerFieldValue('birthDate', date ? dayjs(date).format('YYYY/MM/DD') : '');
                                  setFieldError('birthDate', null);
                                }}
                                error={getContentKey(errors?.birthDate)}
                              />
                            </ViewReverse >
                          </div >

                          <ViewReverse className={'w-full mt-5 justify-end gap-2'} isResponsive>
                            <Button
                              text={getContentKey('clear')}
                              className={'w-full md:w-44 bg-white border border-grey text-black'}
                              weight={TextWeight.bold}
                              textSize={fontSizes.h14}
                              isDisabled={!is_rejected && generalInformationData?.IsManagerListFetchByWathq}
                              onClick={resetForm}
                            />
                            <Button
                              text={getContentKey('add')}
                              className={'w-full md:w-44 bg-light-blue border-none'}
                              weight={TextWeight.bold}
                              textSize={fontSizes.h14}
                              isDisabled={!is_rejected && generalInformationData?.IsManagerListFetchByWathq}
                              onClick={handleSubmit}
                            />
                          </ViewReverse>
                        </>
                      )
                    }}
                  </Formik >
                </div>
              )
              }

              <ViewReverse className={'gap-6 mt-8'} isResponsive>
                <TextArea
                  label={getContentKey('name_of_neutral_persons')}
                  isRequired
                  isLabelHeight
                  isArabicAllowed
                  maxLength={100}
                  labelDivClass={'md:h-8 lg:h-8'}
                  placeholder={getContentKey('enter_name_of_neutral_persons')}
                  value={values?.nameOfAllNaturalPersonsWhoOwnOrControlCompanyShares}
                  onChange={(text) => {
                    if (alphabet_regix.test(text)) {
                      setFieldValue('nameOfAllNaturalPersonsWhoOwnOrControlCompanyShares', text);
                      setFieldError('nameOfAllNaturalPersonsWhoOwnOrControlCompanyShares', null);
                    }
                  }}
                  error={getContentKey(errors?.nameOfAllNaturalPersonsWhoOwnOrControlCompanyShares)}
                  remarksMetadata={values?.nameOfAllNaturalPersonsWhoOwnOrControlCompanyShares !== generalInformationData?.nameOfAllNaturalPersonsWhoOwnOrControlCompanyShares ? null : {
                    sectionId: 2,
                    key: 'nameOfAllNaturalPersonsWhoOwnOrControlCompanyShares',
                  }}
                />
                <div className='w-1/2 hidden md:flex' />
              </ViewReverse>
            </>
          )
          )
        }}
      </Formik >

      <ManagerEditModal
        isOpen={editManagerModal}
        onClose={handleCloseEditManagerModal}
        getContentKey={getContentKey}
        getEditManagerInitialValues={getEditManagerInitialValues}
        handleEditManagerSubmit={handleEditManagerSubmit}
        corporate_dropdown_data={corporate_dropdown_data}
        corporate_loading={corporate_loading}
        isManagerListFetchByWathq={generalInformationData?.isManagerListFetchByWathq}
      />
    </div >
  );
};

export default GeneralInformation;
